/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react';
import { plusicon, refreshicon } from '../../components/imagepath';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import moment from 'moment';
import { CustomTable } from '../../components/Table';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import { ActionGetLeavesList } from '../../store/Actions/StaffAction';
import { getAdminuUerDetails } from '../../store/Actions/AuthActions';
import { ActionUpdateById } from '../../store/Actions/CommonAction';
import { matchPermissions } from '../../utils/Permission';
import { message as antMessage, Select, Tag } from 'antd';

const Leave = () => {
  const dispatch = useDispatch();
  const { userDetails } = useSelector(state => state?.auth);
  const userId = userDetails?.[0]?._id;
  const pageSize = 10;
  const [Refresh, setRefresh] = useState(false);
  const [error, setError] = useState(null);
  const params = queryString.parse(location.search);

  const { leavesList, loader } = useSelector(state => state?.Staff);

  useEffect(() => {
    const fetchLeaves = async () => {
      try {
        setError(null);
        await dispatch(ActionGetLeavesList({ Query: `?page=${params?.page || 1}` }));
        setRefresh(false);
      } catch (err) {
        console.error('Error fetching leaves:', err);
        setError('Failed to load leave requests. Please try again.');
        antMessage.error('Failed to load leave requests');
      }
    };

    fetchLeaves();
  }, [Refresh, params?.page, dispatch]);

  useEffect(() => {
    try {
      dispatch(getAdminuUerDetails());
    } catch (err) {
      console.error('Error fetching user details:', err);
    }
  }, [userId, dispatch]);

  const matchPermissionsForUser = matchPermissions(userDetails);
  const canUpdate = matchPermissionsForUser('leave.update');
  const canCerate = matchPermissionsForUser('leave.create');

  const statusOptions = [
    { value: 'Pending', label: 'Pending', color: 'orange' },
    { value: 'Approved', label: 'Approved', color: 'green' },
    { value: 'Cancelled', label: 'Cancelled', color: 'red' },
  ];

  const handleDropdownChange = async (id, status) => {
    try {
      dispatch(
        ActionUpdateById(
          { Query: `admin/leaves/${id}`, obj: { status: status } },
          response => {
            if (response && response.statusCode === 200) {
              antMessage.success(`Leave status updated to ${status}`);
              setRefresh(true);
            } else {
              antMessage.error('Failed to update leave status');
            }
          }
        )
      );
    } catch (error) {
      console.error('Error updating leave status:', error);
      antMessage.error('An error occurred while updating leave status');
    }
  };

  const renderStatusDropdown = (id, text) => {
    // Find the current status option for styling
    const currentOption = statusOptions.find(opt => opt.value === text);

    return (
      <div>
        {canUpdate && text === 'Pending' ? (
          <Select
            value={text}
            onChange={(value) => handleDropdownChange(id, value)}
            style={{ width: '130px' }}
            options={statusOptions}
            dropdownStyle={{
              zIndex: 9999,
              maxHeight: '300px'
            }}
            listHeight={300}
            dropdownClassName="leave-status-dropdown"
            placement="bottomLeft"
          />
        ) : (
          <Tag color={currentOption?.color || 'default'}>
            {text}
          </Tag>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: 'EmployeeName',
      dataIndex: 'adminName',
      sorter: (a, b) => (a?.adminName || '').localeCompare(b?.adminName || ''),
      render: (text) => text || '---',
    },
    {
      title: 'LeaveType',
      dataIndex: 'leaveType',
      sorter: (a, b) => (a?.leaveType || '').localeCompare(b?.leaveType || ''),
      render: (text) => text || '---',
    },
    {
      title: 'From',
      dataIndex: 'fromDate',
      sorter: (a, b) => new Date(a?.fromDate || 0) - new Date(b?.fromDate || 0),
      render: (text, record) => {
        return <div>{(text && moment(text).format('DD/MM/YYYY')) || '---'}</div>;
      },
    },
    {
      title: 'To',
      dataIndex: 'toDate',
      sorter: (a, b) => new Date(a?.toDate || 0) - new Date(b?.toDate || 0),
      render: (text, record) => {
        return <div>{(text && moment(text).format('DD/MM/YYYY')) || '---'}</div>;
      },
    },
    {
      title: 'No of Days',
      dataIndex: 'noOfDays',
      sorter: (a, b) => (a?.noOfDays || 0) - (b?.noOfDays || 0),
      render: (text, record) => {
        const days = text || 0;
        const isHalfDay = record?.isHalfDay;
        return (
          <div>
            {days} {isHalfDay && <span className="badge badge-info ms-1">Half Day</span>}
          </div>
        );
      },
    },
    {
      title: 'Start Time',
      dataIndex: 'startTime',
      render: (text) => text || '--',
    },
    {
      title: 'End Time',
      dataIndex: 'endTime',
      render: (text) => text || '---',
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      sorter: (a, b) => (a?.reason || '').localeCompare(b?.reason || ''),
      render: (text) => (
        <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={text}>
          {text || '---'}
        </div>
      ),
    },
    ...(canUpdate ? [{
      title: 'Status',
      dataIndex: 'status',
      render: (text, record) => {
        return renderStatusDropdown(record._id, text);
      },
    }] : []),
  ];

  // Empty state component
  const EmptyState = () => (
    <div className="text-center py-5">
      <FeatherIcon icon="inbox" size={48} className="text-muted mb-3" />
      <h5 className="text-muted">No Leave Requests Found</h5>
      <p className="text-muted">There are no leave requests to display.</p>
      {canCerate && (
        <Link to="/add-leave" className="btn btn-primary mt-3">
          <img src={plusicon} alt="#" className="me-2" />
          Add Leave Request
        </Link>
      )}
    </div>
  );

  // Error state component
  const ErrorState = () => (
    <div className="text-center py-5">
      <FeatherIcon icon="alert-circle" size={48} className="text-danger mb-3" />
      <h5 className="text-danger">Error Loading Leave Requests</h5>
      <p className="text-muted">{error}</p>
      <button onClick={() => setRefresh(true)} className="btn btn-primary mt-3">
        <FeatherIcon icon="refresh-cw" size={16} className="me-2" />
        Retry
      </button>
    </div>
  );

  return (
    <>
      <div className="main-wrapper">
        <Header />
        <Sidebar id="menu-item3" id1="menu-items3" activeClassName="leaves" />
        <div className="page-wrapper">
          <div className="content">
            {/* Page Header */}
            <div className="page-header">
              <div className="row">
                <div className="col-sm-12">
                  <ul className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/leaves">Staffs </Link>
                    </li>
                    <li className="breadcrumb-item">
                      <i className="feather-chevron-right">
                        <FeatherIcon icon="chevron-right" />
                      </i>
                    </li>
                    <li className="breadcrumb-item active">Leave Request</li>
                  </ul>
                </div>
              </div>
            </div>
            {/* /Page Header */}
            <div className="row">
              <div className="col-sm-12">
                <div className="card card-table show-entire">
                  <div className="card-body">
                    {/* Table Header */}
                    <div className="page-table-header mb-2">
                      <div className="row align-items-center">
                        <div className="col">
                          <div className="doctor-table-blk">
                            <h3>Leave Request</h3>
                            <div className="doctor-search-blk">
                              <div className="add-group">
                                {canCerate && (
                                  <Link to="/add-leave" className="btn btn-primary add-pluss ms-2">
                                    <img src={plusicon} alt="#" />
                                  </Link>
                                )}

                                <Link
                                  onClick={() => setRefresh(true)}
                                  className="btn btn-primary doctor-refresh ms-2"
                                >
                                  <img src={refreshicon} alt="#" />
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Table Content */}
                    <div className="table-responsive">
                      {error ? (
                        <ErrorState />
                      ) : !loader && (!leavesList?.[0]?.docs || leavesList[0].docs.length === 0) ? (
                        <EmptyState />
                      ) : (
                        <CustomTable
                          CurrentPageNum={leavesList?.[0]?.page || 1}
                          OnChange={v => {
                            var newp = params;
                            newp.page = v;
                            window.location.href =
                              window.location.pathname + '?' + queryString.stringify(newp);
                          }}
                          TotalPageList={leavesList?.[0]?.totalDocs || 0}
                          column={columns}
                          dataSource={leavesList?.[0]?.docs || []}
                          dataSourceLength={leavesList?.[0]?.docs?.length || 0}
                          pageSize={10}
                          loader={loader}
                          ShowHeader={false}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Leave;
